import React, { useCallback, useEffect, useState } from 'react';

import Table, { IColumn } from '~/components/Table';
import api from '~/services/api';
import ExportCsv from '../ExportCsv';
import Loading from '../Loading';

import { KitsSold } from './styles';
import ExportXls from '../ExportXlsx';

interface IKitResponse {
  female: number;
  male: number;
  kit2_quantity_id: number;
  event_id: number;
  event_name: string;
  size: string;
  active: boolean;
  quantity_preset: number;
  quantity_used: number;
  balance: string;
  quantity_tickets: string;
  tickets_sale: number;
  tickets_cortesy: number;
}

interface IPagedResponse {
  data: IKitResponse[];
  from: number;
  to: number;
  total: number;
  pages: number;
}

interface IKitRow {
  id: number;
  event: string;
  sizes: string;
  active: string;
  total: number;
  solds: number;
  paid: number;
  courtesy: number;
  balance: number;
}

interface IDashboardKitsSoldProps {
  selectedEventId?: number | null;
}

const columns: IColumn[] = [
  {
    name: 'Evento',
    selector: 'event',
    sortable: false,
    cell: (row) => <span title={row.event}>{row.event}</span>,
  },
  {
    name: 'Tamanhos',
    selector: 'sizes',
    sortable: false,
    cell: (row) => <span title={row.sizes}>{row.sizes}</span>,
  },
  {
    name: 'Ativo',
    selector: 'active',
    sortable: false,
  },
  {
    name: 'Estoque',
    selector: 'total',
    sortable: false,
  },
  {
    name: 'Vendido',
    selector: 'solds',
    sortable: false,
  },
  {
    name: 'Pago',
    selector: 'paid',
    sortable: false,
  },
  {
    name: 'Cortesia',
    selector: 'courtesy',
    sortable: false,
  },
  {
    name: 'Saldo',
    selector: 'balance',
    sortable: false,
  },
];

const DashboardKitsSold: React.FC<IDashboardKitsSoldProps> = ({
  selectedEventId,
}) => {
  const [kits, setKits] = useState<IKitRow[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedPage, setSelectedPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const handleLoad = useCallback(
    async (page: number, eventId?: number | null) => {
      const response = await api.get<IPagedResponse>('/sales/organizers/kits', {
        params: { page, event_id: eventId },
      });

      const mapped = response.data.data.map<IKitRow>((item) => ({
        id: item.kit2_quantity_id,
        event: item.event_name,
        sizes: item.size.includes('_BABY')
          ? `Baby: ${item.size.replace('_BABY', '')}`
          : !Number.isNaN(parseInt(item.size, 10))
          ? `${item.size} Anos`
          : item.size,
        active: item.active ? 'Sim' : 'Não',
        total: item.quantity_preset,
        solds: parseInt(item.quantity_tickets, 10) || 0,
        paid: item.tickets_sale || 0,
        courtesy: item.tickets_cortesy || 0,
        balance: parseInt(item.balance, 10) || 0,
      }));

      setKits(mapped);
      setTotal(response.data.total);
      setTotalPages(response.data.pages);
    },
    []
  );

  useEffect(() => {
    setSelectedPage(1);
    if (selectedEventId) {
      handleLoad(1, selectedEventId);
    } else {
      setKits([]);
      setTotal(0);
      setTotalPages(0);
    }
  }, [handleLoad, selectedEventId]);

  const handleChangePage = useCallback(
    (page: number) => {
      if (page <= totalPages) {
        setSelectedPage(page);
        handleLoad(page, selectedEventId);
      }
    },
    [handleLoad, selectedEventId, totalPages]
  );

  const handleClickExportCsv = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/sales/organizers/kits', {
        params: { event_id: selectedEventId },
      });

      const mapped = (response.data ?? []).map((item: any) => ({
        event: item.event_name,
        sizes: item.size.includes('_BABY')
          ? `Baby: ${item.size.replace('_BABY', '')}`
          : !Number.isNaN(parseInt(item.size, 10))
          ? `${item.size} Anos`
          : item.size,
        active: item.active ? 'Sim' : 'Não',
        total: parseInt(item.quantity_preset, 10) || 0,
        solds: parseInt(item.quantity_tickets, 10) || 0,
        paid: parseInt(item.tickets_sale, 10) || 0,
        courtesy: parseInt(item.tickets_cortesy, 10) || 0,
        balance: parseInt(item.balance, 10) || 0,
      }));
      return mapped;
    } catch (err) {
      return [];
    } finally {
      setLoading(false);
    }
  }, [selectedEventId]);

  return (
    <KitsSold className="col-12 mt-3 px-0">
      <div className="zoom bg-white p-3">
        <div className="d-flex align-items-center justify-content-between">
          <h2 className="h4 fw-semibold mb-0">Kits Vendidos</h2>
          <div>
            <ExportCsv
              data={kits}
              columns={columns}
              fileName="Kits_vendidos"
              onClick={handleClickExportCsv}
            />
            <ExportXls
              data={kits}
              columns={columns}
              fileName="Kits_vendidos"
              onClick={handleClickExportCsv}
              className="export-xls"
            />
          </div>
        </div>
        <hr />
        <div className="">
          <Table
            className="table p-0 mb-0"
            columns={columns}
            data={kits}
            totalData={total}
            pagination
            selectedPage={selectedPage}
            onChangePage={handleChangePage}
          />
        </div>
      </div>
      <Loading active={loading} text="Carregando..." />
    </KitsSold>
  );
};

export default DashboardKitsSold;
